import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageHeader, SectionHeader } from "../components/ui-blocks";
import { IMAGES } from "../lib/content";
import { ArrowRight } from "lucide-react";

export default function Insights() {
  const { t } = useTranslation();
  const articles = t("data.articles", { returnObjects: true });

  const CATEGORIES = [
    { key: "all", label: t("insights.categories.all") },
    { key: "africa", label: t("insights.categories.africa") },
    { key: "emerging", label: t("insights.categories.emerging") },
    { key: "telecom", label: t("insights.categories.telecom") },
    { key: "energy", label: t("insights.categories.energy") },
    { key: "tourism", label: t("insights.categories.tourism") },
  ];

  const [activeKey, setActiveKey] = useState("all");
  const activeLabel = CATEGORIES.find((c) => c.key === activeKey)?.label;

  const filtered =
    activeKey === "all"
      ? articles
      : articles.filter((a) => a.category === activeLabel);

  const [featured, ...rest] = articles;

  return (
    <>
      <PageHeader
        testId="insights-header"
        overline={t("insights.headerOverline")}
        title={t("insights.headerTitle")}
        lead={t("insights.headerLead")}
      />

      {/* FEATURED */}
      <section className="section-pad" data-testid="insights-featured">
        <div className="container-mtbl">
          <Link
            to={featured.slug ? `/insights/${featured.slug}` : "#"}
            data-testid="insights-featured-link"
            className={`grid lg:grid-cols-12 gap-0 items-stretch rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-[0_10px_30px_rgba(11,26,61,0.06)] group ${
              featured.slug
                ? "cursor-pointer hover:shadow-[0_20px_50px_rgba(11,26,61,0.12)] transition-shadow"
                : "pointer-events-none"
            }`}
          >
            <div className="lg:col-span-7 relative aspect-[16/10] lg:aspect-auto lg:min-h-[480px] overflow-hidden">
              <img
                src={IMAGES[featured.img]}
                alt={featured.title}
                className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
              />
            </div>
            <div className="lg:col-span-5 p-8 md:p-12 flex flex-col justify-center">
              <p className="overline">{featured.category}</p>
              <h2 className="mt-5 text-[#0B1A3D] text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight leading-tight">
                {featured.title}
              </h2>
              <p className="mt-5 text-slate-600 text-base md:text-lg leading-relaxed">
                {featured.excerpt}
              </p>
              <div className="mt-8 flex items-center justify-between">
                <span className="text-xs text-slate-500 tracking-wide uppercase">
                  {featured.date}
                </span>
                <span className="inline-flex items-center gap-2 text-[#0B1A3D] text-sm font-semibold">
                  {t("common.readPerspective")} <ArrowRight size={16} />
                </span>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* FILTER */}
      <section
        data-testid="insights-filter"
        className="bg-paper border-y border-slate-200"
      >
        <div className="container-mtbl py-10">
          <div className="flex flex-wrap gap-3">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                onClick={() => setActiveKey(c.key)}
                data-testid={`insights-filter-${c.key}`}
                className={`text-xs md:text-sm px-5 py-2.5 rounded-full border tracking-wide transition-colors ${
                  activeKey === c.key
                    ? "bg-[#0B1A3D] text-white border-[#0B1A3D]"
                    : "border-slate-300 text-[#0B1A3D] hover:border-[#0B1A3D] bg-white"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ARTICLE GRID */}
      <section className="section-pad" data-testid="insights-grid">
        <div className="container-mtbl">
          <SectionHeader
            overline={t("insights.gridOverline")}
            title={t("insights.gridTitle")}
          />
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(activeKey === "all" ? rest : filtered).map((a, i) => {
              const Wrapper = a.slug ? Link : "article";
              const wrapperProps = a.slug ? { to: `/insights/${a.slug}` } : {};
              return (
                <Wrapper
                  key={a.title}
                  data-testid={`insights-card-${i}`}
                  className={`group card-soft overflow-hidden block ${
                    a.slug ? "cursor-pointer" : ""
                  }`}
                  {...wrapperProps}
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={IMAGES[a.img]}
                      alt={a.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-7 md:p-8">
                    <div className="flex items-center justify-between text-xs tracking-wide uppercase">
                      <span className="text-slate-500">{a.category}</span>
                      <span className="text-slate-400">{a.date}</span>
                    </div>
                    <h3 className="mt-4 text-[#0B1A3D] text-lg md:text-xl font-semibold tracking-tight leading-snug">
                      {a.title}
                    </h3>
                    <p className="mt-3 text-slate-600 text-sm leading-relaxed line-clamp-3">
                      {a.excerpt}
                    </p>
                    <div className="mt-6 inline-flex items-center gap-2 text-[#0B1A3D] text-sm font-semibold">
                      {t("common.readMore")} <ArrowRight size={16} />
                    </div>
                  </div>
                </Wrapper>
              );
            })}
          </div>
          {activeKey !== "all" && filtered.length === 0 && (
            <p
              className="mt-14 text-slate-500 text-center"
              data-testid="insights-empty"
            >
              {t("insights.empty")}
            </p>
          )}
        </div>
      </section>
    </>
  );
}
